import React, { useState } from 'react'
import { View, Text, StyleSheet, TouchableOpacity, Image, ScrollView } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { AddButton, SearchBar } from '../../../components/molecules'
import { Icon } from '../../../components/atoms'
import { Theme } from '@theme'
import { getCommonStyles, Scope } from '@utils'
import ContactsFilter from './ContactsFilter'
import { Contact, usePaginateContacts, useScreen } from '@hooks'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'
import { useLoadMoreOnScroll } from './useLoadMoreOnScroll'

const PAGE_SIZE = 20

interface ContactsListProps {
  isEdit?: boolean
  data?: Contact[]
  fitParent?: boolean
  title?: string
  multiple?: boolean
  selectedContactIds: string[]
  setSelectedContactIds: React.Dispatch<React.SetStateAction<string[]>>
  renderSelectedContact?: (contactId: string) => React.ReactNode
  onAddContact?: () => void
  // When true, keep the Ghost scope tab and stop filtering ghost profiles out
  // even in single-select mode (default single-select hides ghosts).
  includeGhost?: boolean
  // Notifies the parent of the tapped contact. Lets a host screen react to the
  // selection (e.g. navigate) instead of only tracking selected ids.
  onSelectContact?: (contact: Contact) => void
  // Notifies the parent when the search field gains/loses focus. Lets a host
  // screen collapse the content above the list while searching so the search
  // input and contacts stay visible above the keyboard.
  onSearchFocusChange?: (focused: boolean) => void
}

const ContactsList = ({
  isEdit = false,
  data,
  title,
  multiple = true,
  selectedContactIds,
  setSelectedContactIds,
  renderSelectedContact,
  onAddContact,
  fitParent = false,
  includeGhost = false,
  onSelectContact,
  onSearchFocusChange,
}: ContactsListProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isDesktop } = useScreen()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)

  const [searchText, setSearchText] = useState('')
  const [scope, setScope] = useState<Scope[]>([Scope.PRIVATE])

  // Ghost profiles are a separate "bonus" set, not mixed with real contacts:
  // - "Fantôme" selected  -> only ghost profiles (onlyGhost)
  // - "Fantôme" NOT selected -> exclude ghosts entirely (noGhost), and Privé/Pro
  //   filter the real contacts.
  const isGhostScope = scope.includes(Scope.GHOST)
  // Nothing ticked: nobody is shown. Without this the server was asked for
  // contacts with no filter, and answered with the account's own profile.
  const hasNoScope = scope.length === 0
  const {
    flatData: contacts,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = usePaginateContacts({
    scope: isGhostScope
      ? undefined
      : scope.includes(Scope.PRIVATE)
        ? Scope.PRIVATE
        : scope.includes(Scope.PROFESSIONAL)
          ? Scope.PROFESSIONAL
          : undefined,
    search: searchText,
    onlyGhost: isGhostScope || undefined,
    noGhost: !isGhostScope || undefined,
    limit: PAGE_SIZE,
  }, { enabled: !hasNoScope })
  // The list used to stop after the first page: the following ones are asked
  // for while scrolling, and while the people do not fill the list.
  const { onScroll, onListLayout, onContentLayout } = useLoadMoreOnScroll({
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  })

  const toggleContactSelection = (contact: Contact) => {
    onSelectContact?.(contact)
    if (!multiple) {
      setSelectedContactIds([contact.id])
    } else {
      const isCurrentlySelected = selectedContactIds.some((id) => id === contact.id)

      if (isCurrentlySelected) {
        const newSelection = selectedContactIds.filter((id) => id !== contact.id)
        setSelectedContactIds(newSelection)
      } else {
        const newSelection = [...selectedContactIds, contact.id]
        setSelectedContactIds(newSelection)
      }
    }
  }

  const renderContactItem = (contact: Contact) => {
    const isSelected = selectedContactIds.some((id) => id === contact.id)
    return (
      <TouchableOpacity
        key={contact.id}
        style={styles.contactItem}
        onPress={() => toggleContactSelection(contact)}
      >
        <View style={styles.avatarWrapper}>
          <Image
            source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
            style={styles.contactAvatar}
          />
          {/* Badge fantôme : distingue un profil fantôme d'un contact réel. */}
          {contact.isGhost && (
            <View style={styles.ghostBadge} pointerEvents="none">
              <Icon name="ghost" size={10} color={theme.colors.purple} />
            </View>
          )}
        </View>
        <Text style={styles.contactName}>{contact.fullName}</Text>

        {/* Checkbox avec design du GenericSelectionModal */}
        {!data && (
          <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
            {isSelected && <Icon name="check" size={14} color="white" />}
          </View>
        )}
      </TouchableOpacity>
    )
  }

  return (
    <View style={styles.container}>
      {/* Ajouter des contacts */}
      <View style={styles.addContact}>
        <Icon name="people" size={20} color={theme.colors.primary} />
        <View style={styles.addContactText}>
          <Text style={styles.addContactTitle}>
            {title ||
              (isEdit ? t('menuContactsList.linkedContacts') : t('menuContactsList.addContacts'))}
          </Text>
          {multiple && (
            <Text style={styles.addContactSubtitle}>
              {isEdit
                ? t('common.contactCount', { count: selectedContactIds.length })
                : t('common.selectedCount', { count: selectedContactIds.length })}
            </Text>
          )}
        </View>
        {isEdit && onAddContact && <AddButton onPress={onAddContact} />}
      </View>

      {/* Contacts sélectionnés */}
      {isEdit && selectedContactIds.length > 0 && renderSelectedContact && (
        <ScrollView style={styles.selectedContactsContainer}>
          {selectedContactIds.map((id) => renderSelectedContact(id))}
        </ScrollView>
      )}

      {!isEdit && (
        <View style={styles.container}>
          <View
            style={
              isDesktop &&
              !fitParent && [
                commonStyles.searchContainerNonMobileWithoutPaddingLeft,
                commonStyles.gapXxxl,
              ]
            }
          >
            {/* Barre de recherche */}
            <View style={(!isDesktop || fitParent) && styles.searchContainer}>
              <SearchBar
                value={searchText}
                onChangeText={setSearchText}
                onFocus={() => onSearchFocusChange?.(true)}
                onBlur={() => onSearchFocusChange?.(false)}
              />
            </View>
            {/* Filtres */}
            <ContactsFilter
              scope={scope}
              noGhost={!multiple && !includeGhost}
              onScopeChange={setScope}
            />
          </View>

          {/* Liste des contacts */}
          <ScrollView
            testID="contacts-picker-list"
            style={styles.contactsListContainer}
            contentContainerStyle={fitParent && styles.containerWithScrollIndicator}
            showsVerticalScrollIndicator={fitParent}
            onScroll={onScroll}
            scrollEventThrottle={100}
            onLayout={onListLayout}
          >
            {/* No style on this View: it only measures the height of the rows. */}
            <View onLayout={onContentLayout}>
              {(hasNoScope ? [] : contacts)
                ?.filter((c) => multiple || includeGhost || !c.scope?.includes(Scope.GHOST))
                .map(renderContactItem)}
            </View>
          </ScrollView>
        </View>
      )}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    addContact: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 16,
    },
    addContactText: {
      marginHorizontal: 12,
      flexShrink: 1,
      // Wraps by words in a narrow window, never down to one letter per line.
      minWidth: 80,
    },
    addContactTitle: {
      fontSize: 16,
      color: theme.colors.text,
      fontWeight: '500',
    },
    addContactSubtitle: {
      fontSize: 13,
      color: theme.colors.greyBlue,
      marginTop: 2,
    },
    searchContainer: {
      marginBottom: theme.spacing.md,
    },
    selectedContactsContainer: {
      marginBottom: 16,
    },
    selectedContactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 10,
    },
    selectedContactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      marginRight: 12,
    },
    selectedContactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    removeButton: {
      padding: 4,
    },
    hashtagText: {
      fontSize: 16,
      color: theme.colors.greyBlue,
      fontWeight: 'bold',
    },
    contactsListContainer: {
      flex: 1,
      marginTop: 0,
    },
    containerWithScrollIndicator: {
      paddingRight: theme.spacing.md,
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.white,
    },
    saveButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 25,
      paddingVertical: 16,
      alignItems: 'center',
    },
    saveButtonDisabled: {
      backgroundColor: theme.colors.lightGrey,
    },
    saveButtonText: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.white,
    },
    saveButtonTextDisabled: {
      color: theme.colors.greyBlue,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      justifyContent: 'flex-end',
    },
    modalContainer: {
      paddingTop: 16,
      width: '100%',
      backgroundColor: theme.colors.white,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      height: '80%',
    },
    handle: {
      width: 36,
      height: 4,
      backgroundColor: theme.colors.lightGrey,
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 8,
      marginBottom: 8,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
      marginHorizontal: 10,
      gap: 16,
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey20,
    },
    avatarWrapper: {
      position: 'relative',
      marginRight: 12,
    },
    contactAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
    },
    ghostBadge: {
      position: 'absolute',
      right: 0,
      bottom: 0,
      width: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.purple,
      alignItems: 'center',
      justifyContent: 'center',
    },
    contactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.white,
    },
    checkboxSelected: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
  })

export default ContactsList
